import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import {
  Truck, MapPin, Play, DownloadSimple, ArrowsClockwise, Warning, Camera,
  CheckCircle, CircleNotch, MapTrifold, Image as ImageIcon, X,
} from "@phosphor-icons/react";

/**
 * Admin > Dashcam & Timestamps.
 *
 * Flow (matches user spec):
 *  1. Admin picks a Truck OR Route.
 *  2. Table renders every completed stop for that filter — timestamp, GPS,
 *     issue photo (if any), and a Dash Cam column that polls Raven status
 *     and offers ▶ View / ⬇ Download / 🔄 Retry.
 *
 * Both media types are retained for 30 days (per user requirement); the
 * backend enforces the actual retention.
 */
const money = (n) => (n == null ? "—" : Number(n).toLocaleString());

function statusChip(s, reason) {
  const map = {
    ready:     ["bg-[#2E7D32]/15 text-[#2E7D32]", "READY"],
    processing:["bg-[#0288D1]/15 text-[#0288D1]", "PROCESSING"],
    pending:   ["bg-[#F57C00]/15 text-[#F57C00]", "PENDING"],
    retry_scheduled: ["bg-[#F57C00]/15 text-[#F57C00]", "AUTO-RETRYING"],
    failed:    ["bg-[#F57C00]/15 text-[#F57C00]", "AUTO-RETRYING"],
    no_footage:["bg-[#5C738A]/15 text-[#5C738A]", "NO FOOTAGE"],
    unavailable: ["bg-[#5C738A]/15 text-[#5C738A]", "NO RAVEN"],
  };
  const [cls, txt] = map[s] || ["bg-[#5C738A]/10 text-[#5C738A]", "—"];
  // Terminal states (ready / no_footage / unavailable) surface the reason as a
  // tooltip. Transient states show "Auto-retrying…" so admins never think
  // they need to click a button — the backend now loops indefinitely.
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-[0.16em] ${cls}`}
      title={reason || undefined}
    >
      {txt}
    </span>
  );
}

export default function DashcamHistory() {
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [filter, setFilter] = useState({ driver_id: "", route_id: "", days: 30 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [player, setPlayer] = useState(null);  // {url, name}

  // Track active pollers so filter changes / unmount don't leave orphans.
  const pollersRef = useRef(new Map());
  // Generation counter — bumped every time the filter changes or the
  // component unmounts. Any in-flight poll whose generation < the current
  // one drops its response on the floor instead of scheduling another
  // setTimeout for a row that's no longer visible.
  const generationRef = useRef(0);

  useEffect(() => {
    Promise.all([api.get("/users").catch(() => ({ data: [] })), api.get("/routes").catch(() => ({ data: [] }))])
      .then(([u, r]) => {
        const users = Array.isArray(u.data) ? u.data : (u.data?.users || []);
        setDrivers(users.filter((x) => x.role === "driver"));
        setRoutes(Array.isArray(r.data) ? r.data : (r.data?.routes || []));
      });
  }, []);

  async function reload() {
    setLoading(true);
    try {
      const params = { days: filter.days || 30, limit: 200 };
      if (filter.driver_id) params.driver_id = filter.driver_id;
      if (filter.route_id) params.route_id = filter.route_id;
      const { data } = await api.get("/admin/completions", { params });
      // Backend returns { items, total, ... } — but some legacy shapes returned
      // a raw array. Handle both.
      const list = Array.isArray(data) ? data : (data.items || data.completions || []);
      setRows(list);
      // Cancel any pollers left over from the previous filter set before we
      // start new ones — otherwise a slow processing row can spawn duplicates.
      pollersRef.current.forEach((t) => clearTimeout(t));
      pollersRef.current.clear();
      // Bump the generation so any in-flight poll from the previous filter
      // set drops its response instead of scheduling another timeout for a
      // row that's no longer on screen.
      generationRef.current += 1;
      const gen = generationRef.current;
      // Kick off Raven status polls for stops missing terminal state.
      // `failed` is NOT terminal anymore — the backend loops on it — so we
      // keep polling until it flips to `ready` or `no_footage`.
      list.forEach((c) => {
        const rv = c.raven || {};
        if (rv.media_id && !["ready", "no_footage", "unavailable"].includes(rv.status)) {
          pollDashcam(c.id, 0, gen);
        }
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load completions");
    } finally { setLoading(false); }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter.driver_id, filter.route_id, filter.days]);

  async function pollDashcam(cid, attempt = 0, gen = generationRef.current) {
    // Auto-refresh a single row's Raven status until it reaches a truly
    // terminal state (`ready` / `no_footage` / `unavailable`) OR we exceed
    // the on-screen cap. Because the backend now re-runs `failed` clips
    // itself, a `failed` status on a row is expected to flip back to
    // `pending` within one reconcile cycle — we treat it as non-terminal
    // here so the UI keeps polling without an admin lifting a finger.
    // Cap: 60 attempts × 20s = 20 min per view. If the admin scrolls away
    // and comes back, `reload()` restarts the poll cleanly.
    const MAX_ATTEMPTS = 60;
    const INTERVAL_MS = 20000;
    // If the filter changed / we unmounted while this poll was in flight,
    // bail before touching state or scheduling another timeout — otherwise
    // we resurrect an orphan poller for a row that's no longer on screen.
    if (gen !== generationRef.current) {
      pollersRef.current.delete(cid);
      return;
    }
    try {
      const { data } = await api.get(`/dashcam/status/${cid}`);
      // Re-check generation AFTER the await — the filter can change while
      // the request is pending.
      if (gen !== generationRef.current) {
        pollersRef.current.delete(cid);
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === cid ? { ...r, raven: data.raven } : r)));
      const terminal = ["ready", "no_footage", "unavailable"].includes(data?.raven?.status);
      if (terminal || attempt + 1 >= MAX_ATTEMPTS) {
        pollersRef.current.delete(cid);
        return;
      }
    } catch {
      if (gen !== generationRef.current) {
        pollersRef.current.delete(cid);
        return;
      }
    }
    const t = setTimeout(() => pollDashcam(cid, attempt + 1, gen), INTERVAL_MS);
    pollersRef.current.set(cid, t);
  }

  // Clear any pending pollers when the filter changes (reload starts fresh)
  // or the component unmounts.
  useEffect(() => () => {
    generationRef.current += 1;  // invalidate all in-flight polls
    pollersRef.current.forEach((t) => clearTimeout(t));
    pollersRef.current.clear();
  }, []);

  async function retry(cid) {
    try {
      await api.post(`/dashcam/retry/${cid}`);
      toast.success("Dash-cam request re-queued");
      setTimeout(() => pollDashcam(cid), 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    }
  }

  async function viewVideo(cid, label) {
    try {
      const resp = await api.get(`/dashcam/stream/${cid}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPlayer({ url, name: label });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Video not ready yet");
    }
  }

  async function downloadVideo(cid) {
    try {
      const resp = await api.get(`/dashcam/download/${cid}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `zmex-dashcam-${cid.slice(0, 8)}.mp4`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  }

  const driverOf = useMemo(() => {
    const m = new Map(); drivers.forEach((d) => m.set(d.id, d)); return m;
  }, [drivers]);
  const routeOf = useMemo(() => {
    const m = new Map(); routes.forEach((r) => m.set(r.id, r)); return m;
  }, [routes]);

  return (
    <div className="space-y-6" data-testid="dashcam-history-page">
      <div>
        <h1 className="font-heading font-black text-2xl text-[#012340]">Dash Cam & Timestamps</h1>
        <p className="text-xs sm:text-sm text-[#5C738A] mt-1 max-w-2xl">
          Every completed stop for the selected driver/route with its exact timestamp, GPS, issue photo, and 2-minute Raven video (1 min before + 1 min after the driver tapped Complete). Media retained for 30 days.
        </p>
      </div>

      {/* Filter row — stacks on mobile */}
      <div className="bg-white border border-[#012340]/10 rounded-sm p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Driver</label>
            <select data-testid="dc-filter-driver" value={filter.driver_id} onChange={(e) => setFilter({ ...filter, driver_id: e.target.value })} className="w-full mt-0.5 px-3 py-2 rounded-sm border border-[#012340]/20 text-sm">
              <option value="">All drivers</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.username}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Route</label>
            <select data-testid="dc-filter-route" value={filter.route_id} onChange={(e) => setFilter({ ...filter, route_id: e.target.value })} className="w-full mt-0.5 px-3 py-2 rounded-sm border border-[#012340]/20 text-sm">
              <option value="">All routes</option>
              {routes.map((r) => <option key={r.id} value={r.id}>#{r.route_number}{r.name ? ` · ${r.name}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Range</label>
            <select data-testid="dc-filter-days" value={filter.days} onChange={(e) => setFilter({ ...filter, days: Number(e.target.value) })} className="w-full mt-0.5 px-3 py-2 rounded-sm border border-[#012340]/20 text-sm">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={reload} disabled={loading} className="w-full px-3 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.18em] hover:brightness-110 disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
              <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Table (desktop) + card list (mobile) */}
      {loading && rows.length === 0 ? (
        <div className="text-center py-12 text-[#5C738A] text-sm"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#012340]/10 rounded-sm p-8 text-center text-sm text-[#5C738A]">
          No completed stops for this filter yet.
        </div>
      ) : (
        <>
          {/* MOBILE CARDS */}
          <div className="sm:hidden space-y-3">
            {rows.map((c) => {
              const rv = c.raven || {};
              const rt = routeOf.get(c.route_id);
              return (
                <div key={c.id} className="bg-white border border-[#012340]/10 rounded-sm p-3" data-testid={`dc-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-[#012340] truncate">{c.customer_name || c.business_name || "—"}</div>
                      <div className="text-[10px] text-[#5C738A]">{new Date(c.completed_at || c.created_at).toLocaleString()}</div>
                    </div>
                    {c.status === "issue" && <Warning size={14} weight="fill" className="text-[#F57C00] flex-shrink-0" />}
                  </div>
                  <div className="mt-2 text-[10px] text-[#5C738A] flex flex-wrap gap-x-3 gap-y-1">
                    <span><Truck size={11} className="inline mr-1" />{driverOf.get(c.driver_id)?.name || c.driver_name || "—"}</span>
                    <span><MapTrifold size={11} className="inline mr-1" />#{c.route_number || routeOf.get(c.route_id)?.route_number || "—"}</span>
                    {c.gps_lat != null && c.gps_lng != null && <span><MapPin size={11} className="inline mr-1" />{c.gps_lat.toFixed(4)}, {c.gps_lng.toFixed(4)}</span>}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <Camera size={12} className="text-[#012340]" />
                      {statusChip(rv.status || "unavailable", rv.failure_reason)}
                    </div>
                    <div className="flex gap-1">
                      {c.photo_url && <button onClick={() => setPlayer({ img: c.photo_url })} className="p-1.5 rounded-sm border border-[#012340]/15" data-testid={`dc-photo-${c.id}`}><ImageIcon size={12} weight="bold" /></button>}
                      {rv.status === "ready" && <>
                        <button onClick={() => viewVideo(c.id, c.customer_name || c.business_name)} className="p-1.5 rounded-sm bg-[#012340] text-white" data-testid={`dc-view-${c.id}`}><Play size={12} weight="fill" /></button>
                        <button onClick={() => downloadVideo(c.id)} className="p-1.5 rounded-sm border border-[#012340]/20" data-testid={`dc-dl-${c.id}`}><DownloadSimple size={12} weight="bold" /></button>
                      </>}
                      {rv.status === "no_footage" && <button onClick={() => retry(c.id)} className="p-1.5 rounded-sm border border-[#5C738A]/30 text-[#5C738A]" data-testid={`dc-retry-${c.id}`} title="Force a fresh clip request from Raven"><ArrowsClockwise size={12} weight="bold" /></button>}
                      {rv.status === "unavailable" && <button onClick={() => retry(c.id)} className="p-1.5 rounded-sm border border-[#5C738A]/30 text-[#5C738A]" data-testid={`dc-retry-${c.id}`} title="Request footage from Raven"><ArrowsClockwise size={12} weight="bold" /></button>}
                    </div>
                  </div>
                  {rv.status === "no_footage" && rv.failure_reason && (
                    <div className="mt-2 text-[10px] text-[#5C738A] break-words" data-testid={`dc-fail-reason-${c.id}`}>
                      No footage: {rv.failure_reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* DESKTOP TABLE */}
          <div className="hidden sm:block bg-white border border-[#012340]/10 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-[0.18em] text-[#5C738A]">
                <tr>
                  <th className="px-4 py-2 text-left font-bold">When</th>
                  <th className="px-4 py-2 text-left font-bold">Customer</th>
                  <th className="px-4 py-2 text-left font-bold">Driver / Route</th>
                  <th className="px-4 py-2 text-left font-bold">GPS</th>
                  <th className="px-4 py-2 text-left font-bold">Photo</th>
                  <th className="px-4 py-2 text-left font-bold">Dash Cam</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const rv = c.raven || {};
                  const rt = routeOf.get(c.route_id);
                  return (
                    <tr key={c.id} className="border-t border-[#012340]/5 hover:bg-[#012340]/[.02]" data-testid={`dc-row-${c.id}`}>
                      <td className="px-4 py-2 text-[#012340] whitespace-nowrap">
                        <div className="font-mono text-xs">{new Date(c.completed_at || c.created_at).toLocaleString()}</div>
                        {c.status === "issue" && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#F57C00] mt-0.5"><Warning size={10} weight="fill" /> ISSUE</span>}
                      </td>
                      <td className="px-4 py-2 text-[#012340]">{c.customer_name || c.business_name || "—"}</td>
                      <td className="px-4 py-2 text-xs text-[#5C738A]">
                        <div>{driverOf.get(c.driver_id)?.name || c.driver_name || "—"}</div>
                        <div>#{c.route_number || rt?.route_number || "—"}{rt?.name ? ` · ${rt.name}` : ""}</div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-[#5C738A]">{c.gps_lat != null && c.gps_lng != null ? `${c.gps_lat.toFixed(4)}, ${c.gps_lng.toFixed(4)}` : "—"}</td>
                      <td className="px-4 py-2">
                        {c.photo_url ? (
                          <button onClick={() => setPlayer({ img: c.photo_url, name: c.customer_name || c.business_name })} className="inline-flex items-center gap-1 text-xs text-[#012340] hover:underline" data-testid={`dc-photo-${c.id}`}>
                            <ImageIcon size={12} weight="bold" /> View
                          </button>
                        ) : <span className="text-[#5C738A]/50 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {statusChip(rv.status || "unavailable", rv.failure_reason)}
                          {rv.status === "ready" && <>
                            <button onClick={() => viewVideo(c.id, c.customer_name || c.business_name)} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#012340] text-white text-[10px] font-bold uppercase tracking-wider" data-testid={`dc-view-${c.id}`}>
                              <Play size={10} weight="fill" /> View
                            </button>
                            <button onClick={() => downloadVideo(c.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#012340]/20 text-[10px] font-bold uppercase tracking-wider text-[#012340]" data-testid={`dc-dl-${c.id}`}>
                              <DownloadSimple size={10} weight="bold" /> DL
                            </button>
                          </>}
                          {rv.status === "no_footage" && (
                            <button onClick={() => retry(c.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#5C738A]/30 text-[#5C738A] text-[10px] font-bold uppercase tracking-wider" data-testid={`dc-retry-${c.id}`} title="Force a fresh clip request from Raven">
                              <ArrowsClockwise size={10} weight="bold" /> Re-request
                            </button>
                          )}
                          {rv.status === "unavailable" && (
                            <button onClick={() => retry(c.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#5C738A]/30 text-[#5C738A] text-[10px] font-bold uppercase tracking-wider" data-testid={`dc-retry-${c.id}`} title="Request footage from Raven">
                              <ArrowsClockwise size={10} weight="bold" /> Request
                            </button>
                          )}
                        </div>
                        {rv.status === "no_footage" && rv.failure_reason && (
                          <div className="mt-1 text-[10px] text-[#5C738A] break-words max-w-[280px]" data-testid={`dc-fail-reason-${c.id}`}>
                            {rv.failure_reason}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Media viewer modal */}
      {player && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-2 sm:p-6" onClick={() => setPlayer(null)} data-testid="dc-player-modal">
          <div className="relative w-full max-w-4xl bg-black rounded-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPlayer(null)} className="absolute top-2 right-2 z-10 p-2 bg-black/60 text-white rounded-sm"><X size={16} weight="bold" /></button>
            {player.img ? (
              <img src={player.img} alt="" className="w-full max-h-[85vh] object-contain" />
            ) : (
              <video src={player.url} controls autoPlay className="w-full max-h-[85vh]" data-testid="dc-video-player" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
